import type { ComboboxItem, ComboboxParsedItem } from '@mantine/core';

import type { LocationOption, LocationRef, PickerRoom } from './locationPickerModel';
import { visibleLocationValues } from './locationPickerModel';

/**
 * The pure half of the picker's room paging (#472): an app can feed rooms a
 * page at a time (scrolling loads the next page, typing switches to a server
 * search) through `LocationPicker`'s `roomSource`, and resolves the selected
 * rooms by id so the closed field can name them. Addresses and floors are
 * few and stay fully loaded; Mantine renders every option it is given (no
 * virtualisation), so a list that isn't paged is capped while nothing is typed.
 * The data fetching itself stays in each app.
 */

/** Options rendered while nothing is typed, for lists that aren't paged. */
export const OPTION_RENDER_LIMIT = 200;
/** Load the next page this close (px) to the bottom of the dropdown. */
export const NEAR_BOTTOM_PX = 120;

/** The non-selectable rows at the end of the list. */
export const LOADING_MORE_VALUE = '__loading_more__';
export const NARROW_HINT_VALUE = '__type_to_narrow__';
const STATUS_VALUES = new Set([LOADING_MORE_VALUE, NARROW_HINT_VALUE]);
export const isStatusValue = (value: string) => STATUS_VALUES.has(value);

/** The action rows ("+ Create room") after everything else: `__action__:<index>`. */
const ACTION_PREFIX = '__action__:';
export const actionValue = (index: number) => `${ACTION_PREFIX}${index}`;
/** The action index of an option value, or null for any other option. */
export function actionIndex(value: string): number | null {
  if (!value.startsWith(ACTION_PREFIX)) return null;
  const n = Number(value.slice(ACTION_PREFIX.length));
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/** Rows that are not locations: pinned to the end of the list, never filtered. */
const isPinnedValue = (value: string) => isStatusValue(value) || actionIndex(value) != null;

/** `useInfiniteQuery`-style `getNextPageParam`: the next offset, or undefined
 *  when everything is loaded (or a page came back empty: never loop). */
export function nextPageOffset(
  lastPage: { count: number; results: readonly unknown[] },
  allPages: readonly { results: readonly unknown[] }[],
): number | undefined {
  if (lastPage.results.length === 0) return undefined;
  const loaded = allPages.reduce((sum, page) => sum + page.results.length, 0);
  return loaded < lastPage.count ? loaded : undefined;
}

/** What the server is asked to search for: the typed text, or '' to browse.
 *  A single `Select` puts the picked option's label into its search box when
 *  it closes; that is not a search (Mantine doesn't filter on it either). */
export function roomSearchTerm(search: string, selectedLabel?: string | null): string {
  const text = search.trim();
  if (!text) return '';
  if (selectedLabel && search === selectedLabel) return '';
  return text;
}

/** The picked rooms (ids, sorted, deduplicated): fetched with `ids=` so the
 *  closed field and the pills name them even when no loaded page has them. */
export function selectedRoomIds(value: readonly LocationRef[] | LocationRef | null | undefined): number[] {
  const refs = value == null ? [] : Array.isArray(value) ? value : [value as LocationRef];
  return [...new Set(refs.filter((r) => r.kind === 'room').map((r) => r.id))].sort((a, b) => a - b);
}

/** The picked rooms that no loaded room list has. */
export function missingRoomIds(ids: readonly number[], loaded: readonly Pick<PickerRoom, 'id'>[]): number[] {
  const have = new Set(loaded.map((r) => Number(r.id)));
  return ids.filter((id) => !have.has(id));
}

/** The loaded pages plus the rooms fetched by id that they lack. */
export function mergeRooms<T extends PickerRoom>(paged: readonly T[], extra: readonly T[]): T[] {
  const have = new Set(paged.map((r) => String(r.id)));
  return [...paged, ...extra.filter((r) => !have.has(String(r.id)))];
}

/** A caller's own filter over the paged rooms (what the endpoint has no
 *  parameter for). The picked rooms always stay, so the field can name them. */
export function keepRooms<T extends PickerRoom>(
  rooms: readonly T[],
  keep: ((room: T) => boolean) | undefined,
  pickedIds: readonly number[],
): T[] {
  if (!keep) return [...rooms];
  const picked = new Set(pickedIds);
  return rooms.filter((r) => picked.has(Number(r.id)) || keep(r));
}

/**
 * The label a closed single Select shows for its pick, so it is never sent
 * as a search: the option's own label, or (while a room picked off a search
 * page is fetched again by id and isn't an option yet) the label it had when
 * it was picked.
 */
export function pickedOptionLabel(
  value: string | null,
  labelOf: (value: string) => string | undefined,
  lastPicked: { value: string; label: string } | null,
): string | undefined {
  if (value == null) return undefined;
  return labelOf(value) ?? (lastPicked?.value === value ? lastPicked.label : undefined);
}

/** True when a scrolled viewport is within `threshold` px of its end. */
export function isNearBottom(
  el: Pick<HTMLElement, 'scrollTop' | 'scrollHeight' | 'clientHeight'>,
  threshold = NEAR_BOTTOM_PX,
): boolean {
  return el.scrollHeight - el.clientHeight - el.scrollTop <= threshold;
}

/** The "Loading more…" row Mantine gets at the end while pages remain. */
export const loadingMoreItem = (): ComboboxItem => ({ value: LOADING_MORE_VALUE, label: 'Loading more…', disabled: true });

const isGroup = (entry: ComboboxParsedItem): entry is Extract<ComboboxParsedItem, { group: string }> => 'group' in entry;

interface FilterContext {
  /** Every option the model built, by value (for the tree's parents). */
  options: readonly LocationOption[];
  /** Rooms the server returned for the current search: shown as hits even
   *  where the client's word rules would not match them. */
  serverHits?: ReadonlySet<string>;
}

/**
 * The picker's Mantine `filter`.
 * - Typed text: the model's word search (`visibleLocationValues`), plus the
 *   server's room hits, each with the rows above it in the tree.
 * - Nothing typed: everything, capped at `limit` options with a "type to
 *   narrow" row after them (Mantine passes `limit` to a custom filter and
 *   leaves applying it to the filter).
 * The status rows ("Loading more…") and the action rows always stay, last.
 */
export function filterPickerEntries(
  entries: readonly ComboboxParsedItem[],
  search: string,
  limit: number | undefined,
  { options, serverHits }: FilterContext,
): ComboboxParsedItem[] {
  const status = entries.filter((e) => !isGroup(e) && isPinnedValue(e.value));
  const body = entries.filter((e) => isGroup(e) || !isPinnedValue(e.value));

  const visible = visibleLocationValues(options, search);
  if (visible && serverHits && serverHits.size > 0) {
    const byValue = new Map(options.map((o) => [o.value, o]));
    for (const hit of serverHits) {
      for (let o = byValue.get(hit); o && !visible.has(o.value); o = o.parent ? byValue.get(o.parent) : undefined) {
        visible.add(o.value);
      }
    }
  }

  const max = visible || limit == null || !Number.isFinite(limit) ? Infinity : Math.max(0, limit);
  let shown = 0;
  let total = 0;
  const kept: ComboboxParsedItem[] = [];
  for (const entry of body) {
    const items = isGroup(entry) ? entry.items : [entry];
    const hits = visible ? items.filter((item) => visible.has(item.value)) : items;
    total += hits.length;
    const room = Math.max(0, max - shown);
    const take = hits.slice(0, room);
    shown += take.length;
    if (take.length === 0) continue;
    kept.push(isGroup(entry) ? { ...entry, items: take } : take[0]);
  }
  if (total > shown) {
    kept.push({ value: NARROW_HINT_VALUE, label: `Showing ${shown} of ${total}. Type to narrow.`, disabled: true });
  }
  return [...kept, ...status];
}
